import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { openFromUrlInNewTab, captureError } from '@clatter/platform';
import { addNotification } from '../store';
import { useLazyGetAssetPresignedUrlQuery, useLazyGetAssetVersionPresignedUrlQuery } from '../services';

export function useAssetDownload() {
  const dispatch = useDispatch();

  const [triggerGetAssetPresignedUrl, { data: assetPresignedUrlData, error: assetPresignedUrlError, isFetching }] =
    useLazyGetAssetPresignedUrlQuery();

  const [triggerGetAssetVersionPresignedUrl, { data: assetVersionPresignedUrlData, error: assetVersionPresignedUrlError, isFetching: isAssetVersionFetching }] =
    useLazyGetAssetVersionPresignedUrlQuery();

  const downloadAssetById = ({ assetId }: { assetId: number }) => {
    if (!assetId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file. Please try again.',
        }),
      );
      return;
    }

    triggerGetAssetPresignedUrl({ assetId });
  };

  const downloadAssetVersionById = ({ assetId, assetVersionId, }: { assetId: number, assetVersionId: number }) => {
    if (!assetId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file. Please try again.',
        }),
      );
      return;
    }

    if (!assetVersionId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file version. Please try again.',
        }),
      );
      return;
    }

    triggerGetAssetVersionPresignedUrl({ assetId: assetId, versionId: assetVersionId });
  };

  useEffect(() => {
    if (!assetPresignedUrlData) return;

    if (assetPresignedUrlData.data) {
      openFromUrlInNewTab(assetPresignedUrlData.data);
    } else {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file. Please try again.',
        }),
      );
    }
  }, [assetPresignedUrlData, dispatch]);

  useEffect(() => {
    if (!assetVersionPresignedUrlData) return;

    if (assetVersionPresignedUrlData.data) {
      openFromUrlInNewTab(assetVersionPresignedUrlData.data);
    } else {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file version. Please try again.',
        }),
      );
    }
  }, [assetVersionPresignedUrlData, dispatch]);

  useEffect(() => {
    if (assetPresignedUrlError) {
      captureError(assetPresignedUrlError, { message: 'Error fetching presigned URL', component: 'useAssetDownload' });
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file. Please try again.',
        }),
      );
    }
  }, [assetPresignedUrlError, dispatch]);

  useEffect(() => {
    if (assetPresignedUrlError) {
      captureError(assetVersionPresignedUrlError, { message: 'Error fetching presigned asset version URL', component: 'useAssetDownload' });
      dispatch(
        addNotification({
          type: 'error',
          message: 'Couldn\'t download this file version. Please try again.',
        }),
      );
    }
  }, [assetVersionPresignedUrlError, dispatch]);

  return {
    downloadAssetById,
    downloadAssetVersionById,
    isFetching: isFetching || isAssetVersionFetching,
  };
}
